import { network } from "../config";
import { PriceRotation } from "../types/PricingRotation";
import TypeDefaultResponse from "../types/TypeDefaultResponse";
import {
  SessionComment,
  SessionPackage,
  Specializations,
} from "../types/TypeSession";
import {
  AppreciationCertificate,
  Badge,
  DiscountTypes,
  Education,
  EducationAccreditation,
  EducationCertificatesResponse,
  EducationCourse,
  Federation,
  FederationsCertificatesResponse,
  MainSpecializations,
  NewCategory,
  OverLaps,
  ParamsAvailable,
  ProfileFillPercentageResponse,
  Qualification,
  QualificationCertificatesResponse,
  SpecialistData,
  SpecialistFreeTimeType,
  SpecialistLevel,
  SpecialistTag,
  SpecialistTagsDirections,
  SubSpecializations,
  TaxCategory,
  TimeRange,
  TypeChoosableParamsAvailable,
  TypeSpecialistFilter,
  UserUpdates,
} from "../types/TypeSpecialist";
import { TypeUpdatedUserInfo, TypeUserData } from "../types/TypeUsers";
import url from "../utils/formApiUrl";
import {
  UploadUrlRequest,
  UploadUrlResponse,
  uploadStoredFileWithPresignedUrl,
} from "../utils/storedFileReferences";
import customKy from "./kyExtend";

const { specialists } = network;

const baseURL = process.env.REACT_APP_HTTP_CONNECTION_STRING + "specialists/";

const getAuthHeader = (token: string) => {
  return { Authorization: `Bearer ${token}` };
};

type CertificateType =
  | "educationCertificates"
  | "qualificationCertificates"
  | "federationCertificates"
  | "appreciationCertificates";

const getFormDataString = (formData: FormData, key: string) => {
  const value = formData.get(key);
  if (typeof value !== "string") return undefined;
  const normalizedValue = value.trim();
  if (!normalizedValue || normalizedValue === "undefined") return undefined;
  return normalizedValue;
};

const removeUndefinedFields = <T extends Record<string, unknown>>(payload: T) =>
  Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  ) as Partial<T>;

const uploadCertificateFile = async ({
  token,
  specialistId,
  certificateType,
  certificateId,
  file,
}: {
  token: string;
  specialistId: string;
  certificateType: CertificateType;
  certificateId?: string;
  file?: File;
}) => {
  if (!file) return {};

  let reservedCertificateId: string | undefined;
  const fileS3 = await uploadStoredFileWithPresignedUrl({
    file,
    getUploadUrl: async (body) => {
      const response = await SpecialistApi.getCertificateFileUploadUrl(token, {
        ...body,
        specialistId,
        certificateType,
        certificateId,
        slot: "fileS3",
      });
      reservedCertificateId = response.certificateId;
      return response;
    },
  });

  return {
    certificateId: certificateId ?? reservedCertificateId,
    fileS3,
  };
};

export default class SpecialistApi {
  static async update(
    updateData: TypeUpdatedUserInfo,
    token: string,
  ): Promise<
    TypeDefaultResponse & {
      specialistData: SpecialistData;
      profileFillness?: ProfileFillPercentageResponse;
    }
  > {
    return await customKy
      .patch(url(baseURL, specialists.update, { id: updateData._id }), {
        headers: getAuthHeader(token),
        json: { updateData },
      })
      .then((res) => res.json());
  }

  static async getMainSpecializations(token: string): Promise<
    TypeDefaultResponse & {
      mainSpecializations?: MainSpecializations[];
    }
  > {
    return await customKy
      .get(url(baseURL, specialists.getMainSpecializations), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getFilterAvailableParams(
    token: string,
    payload: {
      filters: TypeSpecialistFilter;
      alreadyFetched?: string[];
      excludeProjectSpecialistsId?: string;
    },
  ): Promise<
    TypeDefaultResponse & {
      paramsAvailable?: TypeChoosableParamsAvailable;
    }
  > {
    return await customKy
      .post(url(baseURL, specialists.getFilterAvailableParams), {
        json: payload,
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getFilteredSpecialists(
    token: string,
    payload: {
      filters: TypeSpecialistFilter;
      alreadyFetched?: string[];
      excludeProjectSpecialistsId?: string;
      calculateSessionsAmount?: boolean;
    },
  ): Promise<
    TypeDefaultResponse & {
      paramsAvailable: ParamsAvailable;
      users?: TypeUserData[];
      overlapDays: OverLaps[];
      shuffleSeed: string;
    }
  > {
    return await customKy
      .post(url(baseURL, specialists.getFilteredSpecialists), {
        json: payload,
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getAvailableParams(
    token: string,
    payload: { param: string; filters: TypeSpecialistFilter },
  ): Promise<string[]> {
    return await customKy
      .post(url(baseURL, specialists.getAvailableParams), {
        json: payload,
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getSpecialistTags(
    token: string,
    payload?: {
      parentTagsIds?: string[];
      direction?: SpecialistTagsDirections;
      level?: number;
    },
  ): Promise<TypeDefaultResponse & { tags?: SpecialistTag[] }> {
    return await customKy
      .post(url(baseURL, specialists.getSpecialistTags), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async createEducation(
    token: string,
    payload: {
      educationText: { language: string; text: string }[];
      specialization: string;
      isShown?: boolean;
    },
  ): Promise<TypeDefaultResponse & { education?: Education }> {
    return await customKy
      .post(url(baseURL, specialists.createEducation), {
        json: payload,
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }

  static async createEducationCourse(
    token: string,
    payload: {
      courseText: { language: string; text: string }[];
      isShown?: boolean;
      educationId: string;
    },
  ): Promise<TypeDefaultResponse & { course?: EducationCourse }> {
    return await customKy
      .post(url(baseURL, specialists.createEducationCourse), {
        json: payload,
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }
  static async createEducationAccreditation(
    token: string,
    payload: {
      accreditationText: { language: string; text: string }[];
      isShown?: boolean;
      badgeId?: string;
      courseId: string;
    },
  ): Promise<TypeDefaultResponse & { accreditation?: EducationAccreditation }> {
    return await customKy
      .post(url(baseURL, specialists.createEducationAccreditation), {
        json: payload,
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }

  static async createFederation(
    token: string,
    payload: {
      federationText: { language: string; text: string }[];
      specialization: string;
      badgeId?: string;
    },
  ): Promise<TypeDefaultResponse & { federation?: Federation }> {
    return await customKy
      .post(url(baseURL, specialists.createFederation), {
        json: payload,
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }

  static async createQualification(
    token: string,
    payload: {
      qualificationText: { language: string; text: string }[];
      specialization: string;
      badgeId?: string;
      federationId: string;
    },
  ): Promise<TypeDefaultResponse & { qualification?: Qualification }> {
    return await customKy
      .post(url(baseURL, specialists.createQualification), {
        json: payload,
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }

  static async createFederationBadge(
    token: string,
    payload: FormData,
  ): Promise<TypeDefaultResponse & { badge?: Badge }> {
    const file = payload.get("file");
    let reservedBadgeId: string | undefined;
    const fileS3 =
      file instanceof File
        ? await uploadStoredFileWithPresignedUrl({
            file,
            getUploadUrl: async (body) => {
              const response = await SpecialistApi.getFederationBadgeUploadUrl(
                token,
                body,
              );
              reservedBadgeId = response.badgeId;
              return response;
            },
          })
        : undefined;

    return await customKy
      .post(url(baseURL, specialists.createFederationBadge), {
        json: removeUndefinedFields({
          badgeId: reservedBadgeId,
          fileS3,
          description: getFormDataString(payload, "description"),
          url: getFormDataString(payload, "url"),
        }),
        headers: getAuthHeader(token),
        timeout: 60000,
      })
      .then((res) => res.json());
  }

  static async getIntroVideoUploadUrl(
    token: string,
    specialistId: string,
    payload: UploadUrlRequest & { language: string },
  ): Promise<UploadUrlResponse> {
    return await customKy
      .post(url(baseURL, specialists.getIntroVideoUploadUrl, { id: specialistId }), {
        headers: getAuthHeader(token),
        json: payload,
        timeout: 60000,
      })
      .then((res) => res.json());
  }

  static async getIntroVideoS3SignedUrl(
    token: string,
    specialistId: string,
    filepath: string,
  ): Promise<TypeDefaultResponse & { url?: string }> {
    return await customKy
      .post(url(baseURL, specialists.getIntroVideoS3SignedUrl, { id: specialistId }), {
        headers: getAuthHeader(token),
        json: { filepath },
        timeout: 60000,
      })
      .then((res) => res.json());
  }

  static async getFederationBadgeUploadUrl(
    token: string,
    payload: UploadUrlRequest & { badgeId?: string },
  ): Promise<UploadUrlResponse & { badgeId?: string }> {
    return await customKy
      .post(url(baseURL, specialists.getFederationBadgeUploadUrl), {
        json: payload,
        headers: getAuthHeader(token),
        timeout: 60000,
      })
      .then((res) => res.json());
  }

  static async updateFederationBadge(
    token: string,
    payload: FormData,
  ): Promise<TypeDefaultResponse & { badge?: Badge }> {
    const file = payload.get("file");
    const updateData = JSON.parse(
      getFormDataString(payload, "updateData") ?? "{}",
    ) as {
      _id: string;
      description?: string;
      url?: string;
      isShown?: boolean;
    };
    const normalizedUpdateData = removeUndefinedFields({
      ...updateData,
      description:
        updateData.description && updateData.description.trim()
          ? updateData.description.trim()
          : undefined,
      url:
        updateData.url && updateData.url.trim() !== "undefined"
          ? updateData.url.trim()
          : undefined,
    });
    const fileS3 =
      file instanceof File
        ? await uploadStoredFileWithPresignedUrl({
            file,
            getUploadUrl: (body) =>
              SpecialistApi.getFederationBadgeUploadUrl(token, {
                ...body,
                badgeId: updateData._id,
              }),
          })
        : undefined;

    return await customKy
      .patch(url(baseURL, specialists.updateFederationBadge), {
        json: removeUndefinedFields({
          updateData: normalizedUpdateData,
          fileS3,
        }),
        headers: getAuthHeader(token),
        timeout: 60000,
      })
      .then((res) => res.json());
  }

  static async updateFederation(
    token: string,
    updateData: TypeUpdatedUserInfo,
  ): Promise<TypeDefaultResponse & { federation?: Federation }> {
    return await customKy
      .patch(url(baseURL, specialists.updateFederation), {
        json: { updateData },
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }

  static async updateQualification(
    token: string,
    updateData: TypeUpdatedUserInfo,
  ): Promise<TypeDefaultResponse & { qualification?: Qualification }> {
    return await customKy
      .patch(url(baseURL, specialists.updateQualification), {
        json: { updateData },
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }

  static async updateEducation(
    token: string,
    updateData: TypeUpdatedUserInfo,
  ): Promise<TypeDefaultResponse & { education?: Education }> {
    return await customKy
      .patch(url(baseURL, specialists.updateEducation), {
        json: { updateData },
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }

  static async updateEducationCourse(
    token: string,
    updateData: TypeUpdatedUserInfo,
  ): Promise<TypeDefaultResponse & { course?: EducationCourse }> {
    return await customKy
      .patch(url(baseURL, specialists.updateEducationCourse), {
        json: { updateData },
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }

  static async updateEducationAccreditation(
    token: string,
    updateData: TypeUpdatedUserInfo,
  ): Promise<TypeDefaultResponse & { accreditation?: EducationAccreditation }> {
    return await customKy
      .patch(url(baseURL, specialists.updateEducationAccreditation), {
        json: { updateData },
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }

  static async uploadAppreciationCertificate(
    token: string,
    payload: {
      specialistId: string;
      file: File;
      receiveDate: Date;
      organization: string;
      receivedFor: string;
    },
  ): Promise<TypeDefaultResponse & { certificate?: AppreciationCertificate }> {
    const uploadedFile = await uploadCertificateFile({
      token,
      specialistId: payload.specialistId,
      certificateType: "appreciationCertificates",
      file: payload.file,
    });

    return await customKy
      .post(
        url(baseURL, specialists.uploadAppreciationCertificates, {
          id: payload.specialistId,
        }),
        {
          json: removeUndefinedFields({
            ...uploadedFile,
            receiveDate: payload.receiveDate.toISOString(),
            organization: payload.organization,
            receivedFor: payload.receivedFor,
          }),
          headers: getAuthHeader(token),
          timeout: 300000,
        },
      )
      .then((res) => res.json());
  }
  static async updateAppreciationCertificate(
    token: string,
    payload: {
      userId: string;
      specialistId: string;
      file?: File;
      updateData: TypeUpdatedUserInfo;
    },
  ): Promise<
    TypeDefaultResponse & {
      certificate?: AppreciationCertificate;
    }
  > {
    const uploadedFile = await uploadCertificateFile({
      token,
      specialistId: payload.specialistId,
      certificateType: "appreciationCertificates",
      certificateId: payload.updateData._id,
      file: payload.file,
    });
    const updateData = removeUndefinedFields({
      ...payload.updateData,
      fileS3: uploadedFile.fileS3,
    });

    return await customKy
      .patch(
        url(baseURL, specialists.updateAppreciationCertificate, {
          id: payload.updateData._id,
        }),
        {
          headers: getAuthHeader(token),
          json: {
            userId: payload.userId,
            specialistId: payload.specialistId,
            updateData,
          },
          timeout: 30000,
        },
      )
      .then((res) => res.json());
  }

  static async getCertificateFileUploadUrl(
    token: string,
    payload: UploadUrlRequest & {
      specialistId: string;
      certificateType: CertificateType;
      certificateId?: string;
      slot: "fileS3" | "originalFileS3";
    },
  ): Promise<UploadUrlResponse & { certificateId?: string }> {
    const { specialistId, ...json } = payload;

    return await customKy
      .post(
        url(baseURL, specialists.getCertificateFileUploadUrl, {
          id: specialistId,
        }),
        {
          json,
          headers: getAuthHeader(token),
          timeout: 60000,
        },
      )
      .then((res) => res.json());
  }

  static async getAppreciationCertificates(
    token: string,
    specialistId: string,
    page: number,
    amount: number,
  ): Promise<
    TypeDefaultResponse & {
      certificates?: AppreciationCertificate[];
      total?: number;
    }
  > {
    return await customKy
      .get(
        url(baseURL, specialists.getAppreciationCertificates, {
          id: specialistId,
          page,
          limit: amount,
        }),
        { headers: getAuthHeader(token) },
      )
      .then((res) => res.json());
  }

  static async getAppreciationPublicCertificates(
    specialistId: string,
    page: number,
    amount: number,
  ): Promise<
    TypeDefaultResponse & {
      certificates?: AppreciationCertificate[];
      total?: number;
    }
  > {
    return await customKy
      .get(
        url(baseURL, specialists.getAppreciationPublicCertificates, {
          id: specialistId,
          page,
          limit: amount,
        }),
        {},
      )
      .then((res) => res.json());
  }

  static async getEducationCertificates(
    token: string,
    specialistId: string,
    page: number,
    amount: number,
  ): Promise<
    TypeDefaultResponse & {
      certificates?: EducationCertificatesResponse[];
      total?: number;
    }
  > {
    return await customKy
      .get(
        url(baseURL, specialists.getEducationCertificates, {
          id: specialistId,
          page,
          limit: amount,
        }),
        { headers: getAuthHeader(token) },
      )
      .then((res) => res.json());
  }

  static async getEducationPublicCertificates(
    specialistId: string,
    page: number,
    amount: number,
  ): Promise<
    TypeDefaultResponse & {
      certificates?: EducationCertificatesResponse[];
      total?: number;
    }
  > {
    return await customKy
      .get(
        url(baseURL, specialists.getEducationPublicCertificates, {
          id: specialistId,
          page,
          limit: amount,
        }),
        {},
      )
      .then((res) => res.json());
  }

  static async getFederationCertificates(
    token: string,
    specialistId: string,
    page: number,
    amount: number,
  ): Promise<
    TypeDefaultResponse & {
      certificates?: FederationsCertificatesResponse[];
      total?: number;
    }
  > {
    return await customKy
      .get(
        url(baseURL, specialists.getFederationCertificates, {
          id: specialistId,
          page,
          limit: amount,
        }),
        { headers: getAuthHeader(token) },
      )
      .then((res) => res.json());
  }

  static async getFederationPublicCertificates(
    specialistId: string,
    page: number,
    amount: number,
  ): Promise<
    TypeDefaultResponse & {
      certificates?: FederationsCertificatesResponse[];
      total?: number;
    }
  > {
    return await customKy
      .get(
        url(baseURL, specialists.getFederationPublicCertificates, {
          id: specialistId,
          page,
          limit: amount,
        }),
        {},
      )
      .then((res) => res.json());
  }

  static async getQualificationCertificates(
    token: string,
    specialistId: string,
    page: number,
    amount: number,
  ): Promise<
    TypeDefaultResponse & {
      certificates?: QualificationCertificatesResponse[];
      total?: number;
    }
  > {
    return await customKy
      .get(
        url(baseURL, specialists.getQualificationCertificates, {
          id: specialistId,
          page,
          limit: amount,
        }),
        { headers: getAuthHeader(token) },
      )
      .then((res) => res.json());
  }

  static async getQualificationPublicCertificates(
    specialistId: string,
    page: number,
    amount: number,
  ): Promise<
    TypeDefaultResponse & {
      certificates?: QualificationCertificatesResponse[];
      total?: number;
    }
  > {
    return await customKy
      .get(
        url(baseURL, specialists.getQualificationPublicCertificates, {
          id: specialistId,
          page,
          limit: amount,
        }),
        {},
      )
      .then((res) => res.json());
  }

  static async getAllSpecialistLevels(payload: {
    mainSpecId?: string;
  }): Promise<
    TypeDefaultResponse & {
      levels?: SpecialistLevel[];
    }
  > {
    const params = new URLSearchParams();
    if (payload && payload.mainSpecId) {
      params.append("mainSpecId", payload.mainSpecId);
    }
    return await customKy
      .get(url(baseURL, specialists.getAllSpecialistLevels, {}, params))
      .then((res) => res.json());
  }

  static async getProfileChangeDiff(
    token: string,
    specialistUserId: string,
  ): Promise<
    TypeDefaultResponse & {
      changes?: {
        changedValues: UserUpdates;
        changedAt: Date;
      };
    }
  > {
    return await customKy
      .get(
        url(baseURL, specialists.getProfileChangeDiff, {
          id: specialistUserId,
        }),
        {
          headers: getAuthHeader(token),
        },
      )
      .then((res) => res.json());
  }

  static async getAllEducations(
    token: string,
    payload: {
      filters: {
        specialization?: string[];
        isShown?: boolean;
        text?: string;
      };
      page: number;
      limit: number;
    },
  ): Promise<
    TypeDefaultResponse & {
      educations?: Education[];
      amount: number;
    }
  > {
    return await customKy
      .post(url(baseURL, specialists.getAllEducations), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async getAllEducationCourses(
    token: string,
    payload: {
      filters: {
        education?: string[];
        isShown?: boolean;
      };
      page: number;
      limit: number;
    },
  ): Promise<
    TypeDefaultResponse & {
      courses?: EducationCourse[];
      amount: number;
    }
  > {
    return await customKy
      .post(url(baseURL, specialists.getAllEducationCourses), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async getAllEducationAccreditations(
    token: string,
    payload: {
      filters: {
        educationCourse?: string[];
        isShown?: boolean;
      };
      page: number;
      limit: number;
    },
  ): Promise<
    TypeDefaultResponse & {
      accreditations?: EducationAccreditation[];
      amount: number;
    }
  > {
    return await customKy
      .post(url(baseURL, specialists.getAllEducationAccreditations), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async getAllFederations(
    token: string,
    payload: {
      filters: {
        specialization?: string[];
        isShown?: boolean;
        text?: string;
      };
      page: number;
      limit: number;
    },
  ): Promise<
    TypeDefaultResponse & {
      federations?: Federation[];
      amount: number;
    }
  > {
    return await customKy
      .post(url(baseURL, specialists.getAllFederations), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async getAllQualifications(
    token: string,
    payload: {
      filters: {
        specialization?: string[];
        isShown?: boolean;
        text?: string;
      };
      page: number;
      limit: number;
    },
  ): Promise<
    TypeDefaultResponse & {
      qualifications?: Qualification[];
      amount: number;
    }
  > {
    return await customKy
      .post(url(baseURL, specialists.getAllQualifications), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async createTaxCategory(
    token: string,
    newCategory: NewCategory,
  ): Promise<TypeDefaultResponse & { category?: TaxCategory }> {
    return await customKy
      .post(url(baseURL, specialists.createTaxCategory), {
        json: { newCategory },
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }

  static async deleteTaxCategory(
    token: string,
    categoryId: string,
  ): Promise<TypeDefaultResponse & { category?: TaxCategory }> {
    return await customKy
      .post(url(baseURL, specialists.deleteTaxCategory), {
        json: { categoryId },
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }

  static async updateTaxCategory(
    token: string,
    updateData: TypeUpdatedUserInfo,
  ): Promise<TypeDefaultResponse & { category?: TaxCategory }> {
    return await customKy
      .patch(
        url(baseURL, specialists.updateTaxCategory, { id: updateData._id }),
        {
          json: { updateData },
          headers: getAuthHeader(token),
          timeout: 300000,
        },
      )
      .then((res) => res.json());
  }

  static async getTaxCategories(
    token: string,
    mainSpecializationId?: string,
  ): Promise<
    TypeDefaultResponse & {
      categories?: TaxCategory[];
    }
  > {
    const params = new URLSearchParams();
    if (mainSpecializationId) {
      params.append("mainSpecializationId", mainSpecializationId);
    }
    return await customKy
      .get(url(baseURL, specialists.getTaxCategories, {}, params), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async setSpecialistTaxCategory(
    token: string,
    payload: {
      specialistId: string;
      categoryId?: string;
    },
  ): Promise<TypeDefaultResponse & { category?: TaxCategory }> {
    return await customKy
      .put(url(baseURL, specialists.setSpecialistTaxCategory), {
        json: payload,
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }

  static async addNextLevelPriceRotation(
    token: string,
    formData: FormData,
  ): Promise<TypeDefaultResponse & { priceRotation?: PriceRotation }> {
    return await customKy
      .put(url(baseURL, specialists.addNextLevelPriceRotation), {
        body: formData,
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }

  static async getNextPriceRotation(token: string): Promise<
    TypeDefaultResponse & {
      priceRotation?: PriceRotation;
    }
  > {
    return await customKy
      .get(url(baseURL, specialists.getNextPriceRotation), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getCurrentPriceRotation(token: string): Promise<
    TypeDefaultResponse & {
      priceRotation?: PriceRotation;
    }
  > {
    return await customKy
      .get(url(baseURL, specialists.getCurrentPriceRotation), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getSpecialistsComments(
    userId: string,
    limit: number,
    page: number,
    specialistId?: string,
  ): Promise<
    TypeDefaultResponse & {
      comments: SessionComment[];
      totalAmount: number;
    }
  > {
    const params = new URLSearchParams();
    if (specialistId && specialistId.length) {
      params.append("specialistId", specialistId);
    }

    return await customKy
      .get(
        url(
          baseURL,
          specialists.getSpecialistsComments,
          { id: userId, page, limit },
          params,
        ),
      )
      .then((res) => res.json());
  }

  static async getSpecialistsSessionsAmount(
    userId: string,
    specialistId?: string,
  ): Promise<
    TypeDefaultResponse & {
      amount: number;
    }
  > {
    const params = new URLSearchParams();
    if (specialistId && specialistId.length) {
      params.append("specialistId", specialistId);
    }
    return await customKy
      .get(
        url(
          baseURL,
          specialists.getSpecialistsSessionsAmount,
          { id: userId },
          params,
        ),
      )
      .then((res) => res.json());
  }

  static async getSpecialistSessionPackages(
    token: string,
    specialistUserId: string,
    specialistId?: string,
  ): Promise<
    TypeDefaultResponse & {
      packages?: SessionPackage[] | null;
      overlapDays?: {
        specialist: string;
        overlaps: { day: string; overlapTime: TimeRange[] }[];
      }[];
    }
  > {
    const params = new URLSearchParams();
    if (specialistId && specialistId.length) {
      params.append("specialistId", specialistId);
    }
    return await customKy
      .get(
        url(
          baseURL,
          specialists.getSpecialistSessionPackages,
          { id: specialistUserId },
          params,
        ),
        {
          headers: getAuthHeader(token),
        },
      )
      .then((res) => res.json());
  }
  static async getSpecialistFreeTime(
    token: string,
    specialistUserId: string,
    specialistId: string,
    language: string,
    overlapDaysAmount?: number,
    projectId?: string,
    isFromTreeQuiz?: boolean,
  ): Promise<
    TypeDefaultResponse & {
      overlapDays: SpecialistFreeTimeType[];
      language: string;
      discountPrice: number;
      specialistId: string;
      mainSpecId: string;
      discountType?: DiscountTypes;
      bookingTimeStep: number;
    }
  > {
    const params = new URLSearchParams();
    if (isFromTreeQuiz) {
      params.append("isFromTreeQuiz", isFromTreeQuiz.toString());
    }
    if (overlapDaysAmount) {
      params.append("overlapDaysAmount", overlapDaysAmount.toString());
    }
    if (projectId) {
      params.append("projectId", projectId);
    }
    return await customKy
      .get(
        url(
          baseURL,
          specialists.getSpecialistFreeTime,
          { userId: specialistUserId, id: specialistId, language },
          params,
        ),
        {
          headers: getAuthHeader(token),
        },
      )
      .then((res) => res.json());
  }
  static async updateFederationCertificate(
    payload: {
      file?: File;
      specialistId: string;
      updateData: TypeUpdatedUserInfo;
    },
    token: string,
  ): Promise<
    TypeDefaultResponse & {
      certificate?: FederationsCertificatesResponse | null;
    }
  > {
    const uploadedFile = await uploadCertificateFile({
      token,
      specialistId: payload.specialistId,
      certificateType: "federationCertificates",
      certificateId: payload.updateData._id,
      file: payload.file,
    });
    const updateData = removeUndefinedFields({
      ...payload.updateData,
      fileS3: uploadedFile.fileS3,
    });

    return await customKy
      .patch(
        url(baseURL, specialists.updateFederationCertificate, {
          id: payload.updateData._id,
        }),
        {
          headers: getAuthHeader(token),
          json: {
            specialistId: payload.specialistId,
            updateData,
          },
          timeout: 30000,
        },
      )
      .then((res) => res.json());
  }

  static async updateEducationCertificate(
    payload: {
      file?: File;
      specialistId: string;
      updateData: TypeUpdatedUserInfo;
    },
    token: string,
  ): Promise<
    TypeDefaultResponse & {
      certificate?: EducationCertificatesResponse | null;
    }
  > {
    const uploadedFile = await uploadCertificateFile({
      token,
      specialistId: payload.specialistId,
      certificateType: "educationCertificates",
      certificateId: payload.updateData._id,
      file: payload.file,
    });
    const updateData = removeUndefinedFields({
      ...payload.updateData,
      fileS3: uploadedFile.fileS3,
    });

    return await customKy
      .patch(
        url(baseURL, specialists.updateEducationCertificate, {
          id: payload.updateData._id,
        }),
        {
          headers: getAuthHeader(token),
          json: {
            specialistId: payload.specialistId,
            updateData,
          },
          timeout: 30000,
        },
      )
      .then((res) => res.json());
  }

  static async updateQualificationCertificate(
    payload: {
      file?: File;
      specialistId: string;
      updateData: TypeUpdatedUserInfo;
    },
    token: string,
  ): Promise<
    TypeDefaultResponse & {
      certificate?: QualificationCertificatesResponse | null;
    }
  > {
    const uploadedFile = await uploadCertificateFile({
      token,
      specialistId: payload.specialistId,
      certificateType: "qualificationCertificates",
      certificateId: payload.updateData._id,
      file: payload.file,
    });
    const updateData = removeUndefinedFields({
      ...payload.updateData,
      fileS3: uploadedFile.fileS3,
    });

    return await customKy
      .patch(
        url(baseURL, specialists.updateQualificationCertificate, {
          id: payload.updateData._id,
        }),
        {
          headers: getAuthHeader(token),
          json: {
            specialistId: payload.specialistId,
            updateData,
          },
          timeout: 30000,
        },
      )
      .then((res) => res.json());
  }

  static async getFederations(
    specialization: string,
    language?: string,
  ): Promise<
    TypeDefaultResponse & {
      federations?: Federation[];
    }
  > {
    const params = new URLSearchParams();
    if (language) {
      params.append("language", language);
    }
    return await customKy
      .get(
        url(
          baseURL,
          specialists.getFederations,
          {
            specializationId: specialization,
          },
          params,
        ),
        {},
      )
      .then((res) => res.json());
  }

  static async getEducations(
    specialization: string,
    language?: string,
  ): Promise<
    TypeDefaultResponse & {
      educations?: Education[];
    }
  > {
    const params = new URLSearchParams();
    if (language) {
      params.append("language", language);
    }
    return await customKy
      .get(
        url(
          baseURL,
          specialists.getEducations,
          {
            specializationId: specialization,
          },
          params,
        ),
        {},
      )
      .then((res) => res.json());
  }

  static async getEducationCourses(
    educationId?: string,
    language?: string,
  ): Promise<
    TypeDefaultResponse & {
      courses?: EducationCourse[];
    }
  > {
    const params = new URLSearchParams();
    if (educationId) {
      params.append("educationId", educationId);
    }
    if (language) {
      params.append("language", language);
    }
    return await customKy
      .get(url(baseURL, specialists.getEducationCourses, {}, params), {})
      .then((res) => res.json());
  }

  static async getEducationAccreditations(
    educationCourseId?: string,
    language?: string,
  ): Promise<
    TypeDefaultResponse & {
      accreditations?: EducationAccreditation[];
    }
  > {
    const params = new URLSearchParams();
    if (educationCourseId) {
      params.append("educationCourseId", educationCourseId);
    }
    if (language) {
      params.append("language", language);
    }
    return await customKy
      .get(url(baseURL, specialists.getEducationAccreditations, {}, params), {})
      .then((res) => res.json());
  }

  static async getQualifications(
    specialization: string,
    federationId?: string,
    language?: string,
  ): Promise<
    TypeDefaultResponse & {
      qualifications?: Qualification[];
    }
  > {
    const params = new URLSearchParams();
    if (federationId) {
      params.append("federationId", federationId);
    }
    if (language) {
      params.append("language", language);
    }
    return await customKy
      .get(
        url(
          baseURL,
          specialists.getQualifications,
          { specializationId: specialization },
          params,
        ),
        {},
      )
      .then((res) => res.json());
  }

  static async getSubSpecializations(
    token: string,
    relatedMainSpec: string[],
  ): Promise<
    TypeDefaultResponse & {
      subSpecializations?: SubSpecializations[];
    }
  > {
    return await customKy
      .get(
        url(baseURL, specialists.getSubSpecializations, {
          ids: relatedMainSpec,
        }),
        {
          headers: getAuthHeader(token),
        },
      )
      .then((res) => res.json());
  }

  static async mergeEducations(
    token: string,
    paylaod: {
      educationIds?: string[];
      updatedText?: { text: string; language: string }[];
    },
  ): Promise<TypeDefaultResponse & { education?: Education }> {
    return await customKy
      .patch(url(baseURL, specialists.mergeEducations), {
        json: paylaod,
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }

  static async mergeFederations(
    token: string,
    paylaod: {
      federationIds?: string[];
      updatedText?: { text: string; language: string }[];
    },
  ): Promise<TypeDefaultResponse & { federation?: Federation }> {
    return await customKy
      .patch(url(baseURL, specialists.mergeFederations), {
        json: paylaod,
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }

  static async mergeQualifications(
    token: string,
    paylaod: {
      qualificationIds?: string[];
      updatedText?: { text: string; language: string }[];
    },
  ): Promise<TypeDefaultResponse & { qualification?: Qualification }> {
    return await customKy
      .patch(url(baseURL, specialists.mergeQualifications), {
        json: paylaod,
        headers: getAuthHeader(token),
        timeout: 300000,
      })
      .then((res) => res.json());
  }

  static async getSpecialistSessionPackageById(
    token: string,
    packageId: string,
  ): Promise<
    TypeDefaultResponse & {
      package?: SessionPackage | null;
      overlapDays?: {
        specialist: string;
        overlaps: { day: string; overlapTime: TimeRange[] }[];
      }[];
    }
  > {
    return await customKy
      .get(
        url(baseURL, specialists.getSpecialistSessionPackageById, {
          id: packageId,
        }),
        {
          headers: getAuthHeader(token),
        },
      )
      .then((res) => res.json());
  }

  static async getAllSpecialzations(token: string): Promise<
    TypeDefaultResponse & {
      specializations?: Specializations[];
    }
  > {
    return await customKy
      .get(url(baseURL, specialists.getAllSpecialzations), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getProviderEvidenceFileSignedUrl(
    token: string,
    payload: {
      specialistId: string;
      filepath: string;
    },
  ): Promise<TypeDefaultResponse & { url?: string }> {
    return await customKy
      .post(
        url(baseURL, specialists.getProviderEvidenceFileSignedUrl, {
          id: payload.specialistId,
        }),
        {
          json: payload,
          headers: getAuthHeader(token),
          timeout: 300000,
        },
      )
      .then((res) => res.json());
  }
}
